// src/components/admin/dashboard/home/AdminHomeScreen.js
//
// The Admin HomeScreen — a minimal, live-updating overview hub for the True
// Education admin portal. It summarizes the most important information from the
// existing pages and links straight into them. Data comes from homeScreenData.js
// (which reuses the existing portal APIs); diagnostics update in real time via a
// Firestore listener, and the remaining sections poll on a short interval.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { backdropDismiss } from "../../../../utils/backdropDismiss";
import {
  FiCalendar,
  FiUsers,
  FiActivity,
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiUserPlus,
  FiRefreshCw,
  FiExternalLink,
  FiChevronRight,
  FiBookOpen,
  FiClipboard,
  FiCheckCircle,
  FiCoffee,
  FiPlus,
  FiX,
  FiHome,
  FiGrid,
  FiBarChart2,
  FiShield,
  FiSettings,
  FiUser,
  FiRotateCcw,
  FiArrowRight,
  FiEdit3,
} from "react-icons/fi";

import "./AdminHomeScreen.css";
import {
  loadUpcomingSchedule,
  loadStudentSections,
  loadClockStatus,
  correctAutoClockOut,
  loadMissingSessionLogs,
  loadDiagnosticProgress,
  subscribeDiagnosticProgress,
  scheduleItemColor,
  fmtClock,
  fmtMinutes,
  fmtTimeRange,
} from "./homeScreenData";
import { saveStudent } from "../../student/studentFirestore";

// ---------------------------------------------------------------------------
// Shortcut registry — every page an admin can pin to the Home shortcuts bar.
// ---------------------------------------------------------------------------
const SHORTCUT_REGISTRY = [
  { id: "schedule", label: "Schedule", path: "/scheduling/schedule", icon: FiCalendar },
  { id: "students", label: "Student Directory", path: "/students/directory", icon: FiUsers },
  { id: "diagnostics", label: "Diagnostic Reports", path: "/diagnostics/reports", icon: FiActivity },
  { id: "curriculum", label: "Curriculum", path: "/curriculum", icon: FiBookOpen },
  { id: "employees", label: "Employee Directory", path: "/employee/directory", icon: FiUsers },
  { id: "empcal", label: "Employee Calendar", path: "/employee/calendar", icon: FiClock },
  { id: "sessionlog", label: "Session Log", path: "/session/log", icon: FiFileText },
  { id: "progress", label: "Progress Report", path: "/session/progress-report", icon: FiClipboard },
  { id: "payroll", label: "Payroll", path: "/employee/payroll", icon: FiBarChart2 },
  { id: "calendar", label: "Student Calendar", path: "/students/calendar", icon: FiCalendar },
  { id: "subjects", label: "Subjects", path: "/scheduling/subjects", icon: FiGrid },
  { id: "announcements", label: "Announcements", path: "/announcements", icon: FiFileText },
  { id: "access", label: "Access Control", path: "/access-control", icon: FiShield },
  { id: "settings", label: "Settings", path: "/settings", icon: FiSettings },
];
const SHORTCUTS_KEY = "ah_shortcuts_v2";
const DEFAULT_SHORTCUTS = ["schedule", "students", "diagnostics", "empcal", "sessionlog", "progress"];

function loadShortcutIds() {
  try {
    const raw = localStorage.getItem(SHORTCUTS_KEY);
    if (!raw) return DEFAULT_SHORTCUTS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_SHORTCUTS;
    return parsed.filter((id) => SHORTCUT_REGISTRY.some((s) => s.id === id));
  } catch {
    return DEFAULT_SHORTCUTS;
  }
}

const REFRESH_MS = 30000;

function statusPill(status) {
  const s = String(status || "").toLowerCase();
  if (s === "confirmed" || s === "present") return { cls: "ah-pill--green", label: status };
  if (s === "canceled" || s === "cancelled") return { cls: "ah-pill--gray", label: status };
  if (s === "no show" || s === "no_show") return { cls: "ah-pill--red", label: status };
  if (s === "pending") return { cls: "ah-pill--amber", label: status };
  return { cls: "ah-pill--gray", label: status || "—" };
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Friendly day label: Today / Tomorrow / Yesterday / "Mon, Jun 22".
function relDateLabel(date) {
  if (!(date instanceof Date)) return "";
  const today = startOfDay(new Date()).getTime();
  const target = startOfDay(date).getTime();
  const diff = Math.round((target - today) / (24 * 60 * 60 * 1000));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// Group records into day buckets. dir "asc" = soonest first; "desc" = newest first.
function groupByDay(items, getDate, dir = "asc") {
  const map = new Map();
  items.forEach((it) => {
    const d = getDate(it);
    if (!d) return;
    const key = startOfDay(d).getTime();
    if (!map.has(key)) map.set(key, { key, date: d, items: [] });
    map.get(key).items.push(it);
  });
  return [...map.values()].sort((a, b) => (dir === "asc" ? a.key - b.key : b.key - a.key));
}

// Build a direct Day-view deep link: /scheduling/schedule/day/YYYY-MM-DD.
// (buildScheduleRoute only emits a day path when given a weekday viewMode, so we
// construct the canonical day token here to guarantee Day view, not Week view.)
function scheduleDayPath(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "/scheduling/schedule";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `/scheduling/schedule/day/${y}-${m}-${d}`;
}

export default function AdminHomeScreen() {
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState({ items: [] });
  const [conference, setConference] = useState([]);
  const [newStudents, setNewStudents] = useState([]);
  const [clock, setClock] = useState({ clockedIn: [], shifts: [], abnormal: [] });
  const [missingLogs, setMissingLogs] = useState({ missing: [], checked: 0 });
  const [diagnostics, setDiagnostics] = useState({ active: [], completed: [] });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);

  const [shortcutIds, setShortcutIds] = useState(loadShortcutIds);
  const [menu, setMenu] = useState(null); // { x, y, items: [{label, icon, onClick, danger}] }
  const [adding, setAdding] = useState(false);

  // Auto Clock-Out correction modal:
  // { item } = the abnormal clock row being corrected. `correctTime` is the
  // 24h "HH:MM" value bound to the time input.
  const [correctTarget, setCorrectTarget] = useState(null);
  const [correctTime, setCorrectTime] = useState("");
  const [correctSaving, setCorrectSaving] = useState(false);
  const [correctError, setCorrectError] = useState("");

  const todayLabel = useMemo(
    () => new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
    []
  );

  // --- Data loading (everything except diagnostics, which is live) ---
  const loadRest = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshing(true);
    const results = await Promise.allSettled([
      loadUpcomingSchedule({ now: new Date() }),
      loadStudentSections({ now: new Date() }),
      loadClockStatus({ now: new Date() }),
      loadMissingSessionLogs({ now: new Date() }),
    ]);
    const [sched, students, clk, logs] = results;
    if (sched.status === "fulfilled") setSchedule(sched.value);
    if (students.status === "fulfilled") {
      setConference(students.value.conference);
      setNewStudents(students.value.newStudents);
    }
    if (clk.status === "fulfilled") setClock(clk.value);
    if (logs.status === "fulfilled") setMissingLogs(logs.value);
    setLastLoaded(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadRest(false);
    const interval = setInterval(() => loadRest(false), REFRESH_MS);
    const onFocus = () => loadRest(false);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadRest]);

  // Live diagnostics — updates instantly as students progress.
  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = subscribeDiagnosticProgress(setDiagnostics);
    } catch {
      loadDiagnosticProgress({ now: new Date() }).then(setDiagnostics).catch(() => {});
    }
    return () => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    };
  }, []);

  // --- Navigation helpers ---
  const go = (path) => navigate(path);
  const openNewTab = (path) => path && window.open(path, "_blank", "noopener");
  const studentPath = (id) => `/students/directory/${encodeURIComponent(id)}`;
  const openStudent = (id) => navigate(studentPath(id));
  const employeePath = (uid) => `/employee/directory/${encodeURIComponent(uid)}`;
  const openEmployee = (uid) => uid && navigate(employeePath(uid));

  const navClick = (path) => (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    go(path);
  };

  // --- Generic context menu ---
  const openMenu = (e, items) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(false);
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  useEffect(() => {
    if (!menu && !adding) return undefined;
    const close = () => {
      setMenu(null);
      setAdding(false);
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu, adding]);

  // --- Shortcuts management ---
  const persistShortcuts = (ids) => {
    setShortcutIds(ids);
    try {
      localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(ids));
    } catch {
      /* noop */
    }
  };
  const removeShortcut = (id) => persistShortcuts(shortcutIds.filter((x) => x !== id));
  const addShortcut = (id) => {
    if (!shortcutIds.includes(id)) persistShortcuts([...shortcutIds, id]);
    setAdding(false);
  };
  const shortcuts = shortcutIds.map((id) => SHORTCUT_REGISTRY.find((s) => s.id === id)).filter(Boolean);
  const availableToAdd = SHORTCUT_REGISTRY.filter((s) => !shortcutIds.includes(s.id));

  // --- Auto Clock-Out correction ---
  const pad2 = (n) => String(n).padStart(2, "0");
  const openCorrect = (item) => {
    setMenu(null);
    setCorrectError("");
    // Prefill with the clock-in time as a sensible lower bound; the real
    // clock-out is always after the tutor clocked in.
    const seed = item.clockIn instanceof Date ? item.clockIn : null;
    setCorrectTime(seed ? `${pad2(seed.getHours())}:${pad2(seed.getMinutes())}` : "");
    setCorrectTarget(item);
  };
  const closeCorrect = () => {
    setCorrectTarget(null);
    setCorrectTime("");
    setCorrectError("");
    setCorrectSaving(false);
  };
  const saveCorrect = async () => {
    if (!correctTarget) return;
    if (!correctTime) {
      setCorrectError("Please enter a clock-out time.");
      return;
    }
    // Combine the shift's calendar date (matches the payroll record's `date`
    // field) with the entered time, mirroring the Payroll editor's convention.
    const dateStr =
      correctTarget.dateStr ||
      (correctTarget.date instanceof Date ? correctTarget.date.toLocaleDateString("en-US") : "");
    const newClockOut = new Date(`${dateStr} ${correctTime}`);
    if (Number.isNaN(newClockOut.getTime())) {
      setCorrectError("That clock-out time isn't valid.");
      return;
    }
    setCorrectSaving(true);
    setCorrectError("");
    try {
      await correctAutoClockOut({
        recordId: correctTarget.id,
        clockSessionId: correctTarget.clockSessionId,
        newClockOut,
      });
      // Reload clock status so deleted/truncated segments and the dismissed
      // Auto Clock-Out row are all reflected immediately.
      const fresh = await loadClockStatus({ now: new Date() });
      setClock(fresh);
      // Keep the Home sidebar attention badge in sync (one fewer auto clock-out).
      window.dispatchEvent(new Event("admin-attention-refresh"));
      closeCorrect();
    } catch (err) {
      setCorrectError(err?.message || "Failed to update the clock-out time.");
      setCorrectSaving(false);
    }
  };

  // --- Record actions ---
  const skipConference = async (s) => {
    if (!window.confirm(`Skip & restart the 25-hour conference cycle for ${s.name}?`)) return;
    try {
      await saveStudent(s.id, {
        conferenceCycleResetAt: new Date().toISOString(),
        conferenceCycleResetHours: Number(s.totalHours || 0),
      });
      setConference((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      alert(`Failed to skip conference cycle: ${err.message}`);
    }
  };

  // --- Context-menu item builders ---
  const shortcutMenu = (sc) => [
    { label: "Open", icon: <FiChevronRight size={13} />, onClick: () => go(sc.path) },
    { label: "Open in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(sc.path) },
    { label: "Remove from Home", icon: <FiX size={13} />, danger: true, onClick: () => removeShortcut(sc.id) },
  ];
  const scheduleMenu = (item) => {
    const items = [];
    if (item.kind === "session" && item.studentId) {
      items.push({ label: "Open student", icon: <FiUser size={13} />, onClick: () => openStudent(item.studentId) });
      items.push({ label: "Open student in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(studentPath(item.studentId)) });
    }
    items.push({ label: "Go to Schedule on this day", icon: <FiCalendar size={13} />, onClick: () => go(scheduleDayPath(item.start)) });
    items.push({ label: "Open Schedule in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(scheduleDayPath(item.start)) });
    return items;
  };
  const conferenceMenu = (s) => [
    { label: "Open student", icon: <FiUser size={13} />, onClick: () => openStudent(s.id) },
    { label: "Open in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(studentPath(s.id)) },
    { label: "Conference management", icon: <FiUsers size={13} />, onClick: () => go(`${studentPath(s.id)}/conference`) },
    { label: "Skip & restart 25 hrs", icon: <FiRotateCcw size={13} />, danger: true, onClick: () => skipConference(s) },
  ];
  const studentMenu = (s) => [
    { label: "Open student", icon: <FiUser size={13} />, onClick: () => openStudent(s.id) },
    { label: "Open in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(studentPath(s.id)) },
  ];
  const missingLogMenu = (s) => [
    { label: "Go to Session Log", icon: <FiFileText size={13} />, onClick: () => go("/session/log") },
    { label: "Open Session Log in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab("/session/log") },
    { label: "Go to Schedule on this day", icon: <FiCalendar size={13} />, onClick: () => go(scheduleDayPath(s.start)) },
    { label: "Open Schedule in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(scheduleDayPath(s.start)) },
  ];
  const clockMenu = (uid) =>
    uid
      ? [
          { label: "Open employee", icon: <FiUser size={13} />, onClick: () => openEmployee(uid) },
          { label: "Open employee in new tab", icon: <FiExternalLink size={13} />, onClick: () => openNewTab(employeePath(uid)) },
          { label: "Open Payroll", icon: <FiBarChart2 size={13} />, onClick: () => go("/employee/payroll") },
        ]
      : [{ label: "Open Payroll", icon: <FiBarChart2 size={13} />, onClick: () => go("/employee/payroll") }];
  // Auto Clock-Out rows get every standard clock action PLUS a correction action.
  const autoClockMenu = (item) => [
    { label: "Change clock-out time", icon: <FiEdit3 size={13} />, onClick: () => openCorrect(item) },
    ...clockMenu(item.uid),
  ];

  if (loading) {
    return (
      <div className="ah-root">
        <div className="ah-loading">
          <FiRefreshCw className="ah-spin" size={18} /> Loading dashboard…
        </div>
      </div>
    );
  }

  // Schedule grouped by day (soonest first).
  const scheduleDays = groupByDay(schedule.items, (it) => it.start, "asc");
  // Clock history (completed shifts) grouped by day (newest first).
  const clockDays = groupByDay(clock.shifts, (s) => s.clockIn, "desc");
  // Combined "Missing" list: missing session logs + 12 AM auto clock-outs.
  const missingItems = [
    ...missingLogs.missing.map((m) => ({ ...m, mtype: "log", when: m.start })),
    ...clock.abnormal.map((a) => ({ ...a, mtype: "clock", when: a.date })),
  ].sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));

  return (
    <div className="ah-root">
      <div className="ah-inner">
        {/* Compact header */}
        <div className="ah-header">
          <div className="ah-headLeft">
            <FiHome size={16} className="ah-headIcon" />
            <span className="ah-headDate">{todayLabel}</span>
            <span className="ah-liveTag">
              <span className="ah-liveDot" /> Live
            </span>
          </div>
          <div className="ah-headRight">
            {lastLoaded && <span className="ah-updated">Updated {fmtClock(lastLoaded)}</span>}
            <button className="ah-iconBtn" onClick={() => loadRest(true)} disabled={refreshing} title="Refresh now">
              <FiRefreshCw size={14} className={refreshing ? "ah-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Shortcuts (immediately after header) */}
        <div className="ah-quick">
          {shortcuts.map((sc) => {
            const Icon = sc.icon;
            return (
              <a
                key={sc.id}
                href={sc.path}
                className="ah-quickBtn"
                onClick={navClick(sc.path)}
                onContextMenu={(e) => openMenu(e, shortcutMenu(sc))}
              >
                <Icon size={14} />
                {sc.label}
              </a>
            );
          })}

          <div className="ah-addWrap">
            <button
              className="ah-quickBtn ah-quickBtn--add"
              onClick={(e) => {
                e.stopPropagation();
                setAdding((v) => !v);
                setMenu(null);
              }}
              disabled={availableToAdd.length === 0}
              title="Add shortcut"
            >
              <FiPlus size={14} /> Add
            </button>
            {adding && availableToAdd.length > 0 && (
              <div className="ah-addMenu" onClick={(e) => e.stopPropagation()}>
                <div className="ah-addMenuTitle">Add a shortcut</div>
                {availableToAdd.map((sc) => {
                  const Icon = sc.icon;
                  return (
                    <button key={sc.id} className="ah-addMenuItem" onClick={() => addShortcut(sc.id)}>
                      <Icon size={14} /> {sc.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Section grid */}
        <div className="ah-grid">
          {/* Upcoming Schedule & Events (left, grouped by day) */}
          <Card
            title="Upcoming Schedule & Events"
            icon={<FiCalendar size={15} />}
            count={schedule.items.length}
            action={{ label: "Open Schedule", onClick: () => go("/scheduling/schedule") }}
          >
            {scheduleDays.length === 0 ? (
              <Empty icon={<FiCalendar size={16} />} text="Nothing upcoming in the next 7 days." />
            ) : (
              <div className="ah-scroll">
                {scheduleDays.map((day) => (
                  <div key={day.key} className="ah-dayGroup">
                    <div className={`ah-subhead ${relDateLabel(day.date) === "Today" ? "ah-subhead--today" : ""}`}>
                      {relDateLabel(day.date)}
                    </div>
                    <div className="ah-evtList">
                      {day.items.map((item) => {
                        const c = scheduleItemColor(item);
                        const isEvent = item.kind === "event";
                        const when = isEvent ? item.timeLabel : fmtTimeRange(item.start, item.end);
                        return (
                          <div
                            key={item.id}
                            className={`ah-evt ${isEvent ? "ah-evt--event" : ""}`}
                            onClick={() => go(scheduleDayPath(item.start))}
                            onContextMenu={(e) => openMenu(e, scheduleMenu(item))}
                          >
                            <div className="ah-evtTop">
                              <span className="ah-evtDot" style={{ background: c.accent }} />
                              <span className="ah-evtName">{isEvent ? item.title : item.studentName}</span>
                              {/* Sessions convey status via the dot color; only events
                                  need an explicit label to distinguish them. */}
                              {isEvent && <span className="ah-pill ah-pill--blue">Event</span>}
                            </div>
                            <div className="ah-evtBottom">
                              <span className="ah-evtWhen">{when}</span>
                              {!isEvent && (
                                <span className="ah-evtSub">
                                  {item.subject ? <span className="ah-evtSubject">{item.subject} · </span> : null}
                                  <span className="ah-evtTutor">{item.tutorName}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Diagnostic Test Progress (right, live) */}
          <Card
            title="Diagnostic Test Progress"
            icon={<FiActivity size={15} />}
            count={diagnostics.active.length}
            live
            action={{ label: "Reports", onClick: () => go("/diagnostics/reports") }}
          >
            {diagnostics.active.length === 0 && diagnostics.completed.length === 0 ? (
              <Empty icon={<FiActivity size={16} />} text="No diagnostics in progress right now." />
            ) : (
              <div className="ah-scroll">
                {diagnostics.active.length > 0 && (
                  <div className="ah-diagList">
                    {diagnostics.active.map((t) => (
                      <DiagnosticCard key={t.id} t={t} />
                    ))}
                  </div>
                )}
                {diagnostics.completed.length > 0 && (
                  <div style={{ marginTop: diagnostics.active.length ? 14 : 0 }}>
                    <div className="ah-subhead">Recently completed · last 3 days</div>
                    {diagnostics.completed.map((c) => (
                      <div key={c.id} className="ah-row" onClick={() => openNewTab(c.reportUrl)}>
                        <div className="ah-rowMain">
                          <div className="ah-rowTitle">
                            {c.studentName} <span className="ah-pill ah-pill--blue">{c.testType}</span>
                          </div>
                          <div className="ah-rowSub">
                            {scoreLabel(c.scoreSummary)} · {relDateLabel(c.completedAt)} {fmtClock(c.completedAt)}
                          </div>
                        </div>
                        <span className="ah-pill ah-pill--green">
                          <FiCheckCircle size={11} /> Completed
                        </span>
                        {c.reportUrl && <FiExternalLink size={13} className="ah-rowChevron" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Live Clock In / Out (left, grouped by day, compact) */}
          <Card
            title="Live Clock In / Out"
            icon={<FiClock size={15} />}
            count={clock.clockedIn.length}
            action={{ label: "Payroll", onClick: () => go("/employee/payroll") }}
          >
            {clock.clockedIn.length === 0 && clock.shifts.length === 0 ? (
              <Empty icon={<FiClock size={16} />} text="No clock activity in the last 7 days." />
            ) : (
              <div className="ah-scroll ah-scroll--short">
                {clock.clockedIn.length > 0 && (
                  <div className="ah-dayGroup">
                    <div className="ah-subhead ah-subhead--today">On the clock now</div>
                    {clock.clockedIn.map((c) => (
                      <div
                        key={`in-${c.id}`}
                        className="ah-clockRow ah-clockRow--active"
                        onClick={() => openEmployee(c.uid)}
                        onContextMenu={(e) => openMenu(e, clockMenu(c.uid))}
                      >
                        <span className="ah-clockName">{c.name}</span>
                        <span className="ah-clockTimes">
                          in <strong>{fmtClock(c.since)}</strong> · out —
                        </span>
                        <span className="ah-pill ah-pill--green">
                          <span className="ah-liveDot ah-liveDot--green" /> Clocked in
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {clockDays.map((day) => (
                  <div key={day.key} className="ah-dayGroup">
                    <div className="ah-subhead">{relDateLabel(day.date)}</div>
                    {day.items.map((s) => (
                      <div
                        key={`sh-${s.id}`}
                        className="ah-clockRow"
                        onClick={() => openEmployee(s.uid)}
                        onContextMenu={(e) => openMenu(e, clockMenu(s.uid))}
                      >
                        <span className="ah-clockName">{s.name}</span>
                        <span className="ah-clockTimes">
                          in <strong>{fmtClock(s.clockIn)}</strong> · out{" "}
                          <strong>{s.clockOut ? fmtClock(s.clockOut) : "—"}</strong>
                        </span>
                        {s.isAuto ? (
                          <span className="ah-pill ah-pill--red">
                            <FiAlertTriangle size={10} /> Auto
                          </span>
                        ) : (
                          <span className="ah-pill ah-pill--slate">Clocked out</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Combined Missing card (right, compact) */}
          <Card
            title="Missing & Needs Attention"
            icon={<FiAlertTriangle size={15} />}
            count={missingItems.length}
            alert={missingItems.length > 0}
            action={{ label: "Session Log", onClick: () => go("/session/log") }}
          >
            {missingItems.length === 0 ? (
              <Empty icon={<FiCheckCircle size={16} />} text="Nothing missing — all clear." />
            ) : (
              <div className="ah-scroll ah-scroll--short">
                {missingItems.map((m) =>
                  m.mtype === "log" ? (
                    <div
                      key={`log-${m.id}`}
                      className="ah-listRow"
                      onClick={() => go("/session/log")}
                      onContextMenu={(e) => openMenu(e, missingLogMenu(m))}
                    >
                      <div className="ah-listMain">
                        <div className="ah-listName">
                          {m.studentName}
                          {m.subject ? <span className="ah-listSubject"> · {m.subject}</span> : null}
                        </div>
                        <div className="ah-listMeta">
                          {relDateLabel(m.start)} · {fmtTimeRange(m.start, m.end)} · {m.tutorName}
                        </div>
                      </div>
                      <span className="ah-pill ah-pill--red ah-missTag">Missing Session Log</span>
                    </div>
                  ) : (
                    <div
                      key={`clk-${m.id}`}
                      className="ah-listRow"
                      onClick={() => openEmployee(m.uid)}
                      onContextMenu={(e) => openMenu(e, autoClockMenu(m))}
                    >
                      <div className="ah-listMain">
                        <div className="ah-listName">{m.name}</div>
                        <div className="ah-listMeta">
                          {relDateLabel(m.date)} · in {fmtClock(m.clockIn)} <FiArrowRight size={10} /> 12:00 AM
                        </div>
                      </div>
                      <span className="ah-pill ah-pill--red ah-missTag">Auto Clock-Out</span>
                    </div>
                  )
                )}
              </div>
            )}
          </Card>

          {/* Conference Needed (lower) */}
          <Card
            title="Conference Needed"
            icon={<FiUsers size={15} />}
            count={conference.length}
            warn={conference.length > 0}
            action={{ label: "Directory", onClick: () => go("/students/directory") }}
          >
            {conference.length === 0 ? (
              <Empty icon={<FiCheckCircle size={16} />} text="No students are due for a conference." />
            ) : (
              <div className="ah-scroll">
                {conference.map((s) => (
                  <div
                    key={s.id}
                    className="ah-listRow"
                    onClick={() => openStudent(s.id)}
                    onContextMenu={(e) => openMenu(e, conferenceMenu(s))}
                  >
                    <div className="ah-listMain">
                      <div className="ah-listName">{s.name}</div>
                      <div className="ah-listMeta">
                        {s.grade !== "" ? `Grade ${s.grade}` : "—"} · {s.status}
                      </div>
                    </div>
                    <span className="ah-pill ah-pill--amber ah-hoursPill">
                      {s.hoursSinceBaseline.toFixed(0)}/{s.threshold} hrs
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* New Students to Follow Up (lower) */}
          <Card
            title="New Students to Follow Up"
            icon={<FiUserPlus size={15} />}
            count={newStudents.length}
            action={{ label: "Directory", onClick: () => go("/students/directory") }}
          >
            {newStudents.length === 0 ? (
              <Empty icon={<FiUserPlus size={16} />} text="No new sign-ups to follow up on." />
            ) : (
              <div className="ah-scroll">
                {newStudents.map((s) => {
                  const showStatus = String(s.status || "").toLowerCase() !== "signed up";
                  return (
                    <div
                      key={s.id}
                      className="ah-listRow"
                      onClick={() => openStudent(s.id)}
                      onContextMenu={(e) => openMenu(e, studentMenu(s))}
                    >
                      <div className="ah-listMain">
                        <div className="ah-listName">{s.name}</div>
                        <div className="ah-listMeta">
                          {s.grade !== "" ? `Grade ${s.grade}` : "Grade —"} · signed up{" "}
                          {s.signupDate ? relDateLabel(s.signupDate) : "recently"}
                        </div>
                      </div>
                      {showStatus && <span className={`ah-pill ${statusPill(s.status).cls}`}>{s.status}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Auto Clock-Out correction modal */}
      {correctTarget && (
        <div
          className="ah-modalOverlay"
          {...backdropDismiss(() => { if (!correctSaving) closeCorrect(); })}
        >
          <div className="ah-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ah-modalTitle">Change clock-out time</div>
            <div className="ah-modalSub">
              {correctTarget.name} · {relDateLabel(correctTarget.date)} · clocked in{" "}
              {fmtClock(correctTarget.clockIn)}
            </div>
            <label className="ah-modalLabel" htmlFor="ah-correctTime">
              Correct clock-out time
            </label>
            <input
              id="ah-correctTime"
              type="time"
              className="ah-modalInput"
              value={correctTime}
              autoFocus
              disabled={correctSaving}
              onChange={(e) => setCorrectTime(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveCorrect();
                if (e.key === "Escape" && !correctSaving) closeCorrect();
              }}
            />
            <div className="ah-modalHint">
              Updates this clock-out and its payroll, then clears the Auto Clock-Out warning.
            </div>
            {correctError && <div className="ah-modalError">{correctError}</div>}
            <div className="ah-modalActions">
              <button className="ah-modalBtn" onClick={closeCorrect} disabled={correctSaving}>
                Cancel
              </button>
              <button
                className="ah-modalBtn ah-modalBtn--primary"
                onClick={saveCorrect}
                disabled={correctSaving}
              >
                {correctSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating context menu */}
      {menu && (
        <div className="ah-ctxMenu" style={{ top: menu.y, left: menu.x }} onClick={(e) => e.stopPropagation()}>
          {menu.items.map((it, i) => (
            <button
              key={i}
              className={`ah-ctxItem ${it.danger ? "ah-ctxItem--danger" : ""}`}
              onClick={() => {
                it.onClick();
                setMenu(null);
              }}
            >
              {it.icon} {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (diagnostics unchanged)
// ---------------------------------------------------------------------------

function DiagnosticCard({ t }) {
  return (
    <div className="ah-diag">
      <div className="ah-diagHead">
        <span className="ah-diagName">{t.studentName}</span>
        <span className="ah-pill ah-pill--blue">{t.testType}</span>
      </div>

      <div className="ah-diagPrimary">
        <div className="ah-diagEst">
          <div className="ah-diagEstNum">{t.isBreak ? "—" : fmtMinutes(t.estimatedTotalTimeLeftSeconds)}</div>
          <div className="ah-diagEstLabel">Est. time left</div>
        </div>
        <div className="ah-diagSubjects">
          {t.subjects.map((s) => (
            <Gauge key={s.key} pct={s.pct} status={s.status} label={s.label} />
          ))}
        </div>
      </div>

      <div className="ah-diagDetail">
        {t.isBreak ? (
          <span className="ah-diagDetailLine">
            <FiCoffee size={12} /> On break · {fmtMinutes(t.sectionTimeLeftSeconds)} left
          </span>
        ) : (
          <>
            <span className="ah-diagDetailLine">
              <strong>{t.currentSectionLabel}</strong>
            </span>
            <span className="ah-diagDetailLine">
              {t.questionsAnswered}/{t.totalQuestions} questions · {fmtMinutes(t.sectionTimeLeftSeconds)} left in section
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function Gauge({ pct, status, label, size = 54, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const offset = circ * (1 - clamped / 100);
  const color = status === "completed" ? "#16a34a" : clamped > 0 ? "#2563eb" : "#cbd5e1";
  return (
    <div className="ah-gauge">
      <svg width={size} height={size} className="ah-gaugeSvg">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f6" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
        <text x="50%" y="50%" dy="0.35em" textAnchor="middle" className="ah-gaugeText">
          {clamped}%
        </text>
      </svg>
      <div className="ah-gaugeLabel">{label}</div>
      <div className={`ah-gaugeStatus ah-gaugeStatus--${status}`}>
        {status === "completed" ? "Done" : status === "in_progress" ? "In progress" : "Not started"}
      </div>
    </div>
  );
}

function scoreLabel(summary) {
  if (!summary) return "Completed";
  if (summary.totalScore) return `Total ${summary.totalScore}`;
  if (summary.compositeScore) return `Composite ${summary.compositeScore}`;
  if (summary.totalRawScore != null) return `Raw ${summary.totalRawScore}`;
  return "Completed";
}

function Card({ title, icon, count, action, wide, alert, warn, live, children }) {
  const cls = `ah-card ${wide ? "ah-card--wide" : ""} ${alert ? "ah-card--alert" : ""} ${warn ? "ah-card--warn" : ""}`;
  return (
    <section className={cls}>
      <div className="ah-cardHead">
        <div className="ah-cardTitle">
          <span className="ah-titleIcon">{icon}</span>
          {title}
          {typeof count === "number" && <span className="ah-count">{count}</span>}
          {live && (
            <span className="ah-liveTag ah-liveTag--sm">
              <span className="ah-liveDot" /> Live
            </span>
          )}
        </div>
        {action && (
          <button className="ah-link" onClick={action.onClick}>
            {action.label} <FiChevronRight size={13} />
          </button>
        )}
      </div>
      <div className="ah-cardBody">{children}</div>
    </section>
  );
}

function Empty({ icon, text }) {
  return (
    <div className="ah-empty">
      {icon} {text}
    </div>
  );
}
